import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { toast } from "react-toastify";
import api from "../../../api";
import { HOLIDAY_TYPES, detectHolidayType, PURPLE, PURPLE_DARK, PURPLE_SOFT } from "../ResourceAllocationShared";

function HolidayChip({ isHoliday, holidayName }) {
  if (!isHoliday) {
    return (
      <Chip size="small" label="Teaching"
        sx={{ bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 600, border: "1px solid #bbf7d0", height: 22 }} />
    );
  }
  const htype = HOLIDAY_TYPES[detectHolidayType(holidayName)];
  return (
    <Chip size="small" label={`${htype.icon} ${holidayName || htype.label}`}
      sx={{ bgcolor: htype.bg, color: htype.color, fontWeight: 600, border: `1px solid ${htype.border}`, height: 22 }} />
  );
}

export default function AiHolidayModifierDialog({ open, onClose, planId, weeks, drafts, onApply }) {
  const [prompt, setPrompt] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [accepted, setAccepted] = useState(new Set());
  const [error, setError] = useState(null);

  const handleClose = () => {
    setPrompt("");
    setSuggestions([]);
    setAccepted(new Set());
    setError(null);
    onClose();
  };

  const handleGetSuggestions = async () => {
    setSuggesting(true);
    setError(null);
    setSuggestions([]);
    setAccepted(new Set());
    try {
      const r = await api.post(`admin/course-plans/${planId}/holidays/ai-suggest`, { prompt });
      const list = r.data?.suggestions ?? [];
      setSuggestions(list);
      setAccepted(new Set(list.map((s) => s.week_id)));
      if (!list.length) {
        toast.info("AI found no changes to suggest for this request.");
      }
    } catch (e) {
      setError(e?.response?.data?.message || "AI suggestion failed. Please try again.");
    } finally {
      setSuggesting(false);
    }
  };

  const toggleAccept = (weekId) => {
    setAccepted((prev) => {
      const next = new Set(prev);
      next.has(weekId) ? next.delete(weekId) : next.add(weekId);
      return next;
    });
  };

  const toggleAll = () => {
    if (accepted.size === suggestions.length) {
      setAccepted(new Set());
    } else {
      setAccepted(new Set(suggestions.map((s) => s.week_id)));
    }
  };

  const handleApply = () => {
    const changes = {};
    suggestions
      .filter((s) => accepted.has(s.week_id))
      .forEach((s) => {
        changes[s.week_id] = { is_holiday: s.is_holiday, holiday_name: s.holiday_name || "" };
      });
    onApply(changes);
    handleClose();
  };

  const activeHolidays = weeks.filter((w) => {
    const draft = drafts[w.id] ?? { is_holiday: w.is_holiday, holiday_name: w.holiday_name || "" };
    return draft.is_holiday;
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoFixHighIcon sx={{ color: PURPLE, fontSize: 22 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>AI Holiday Modifier</Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {activeHolidays.length > 0 && (
          <>
            <Typography variant="caption" fontWeight={700} sx={{ color: "#64748b", display: "block", mb: 0.75 }}>
              Current holidays
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} mb={1.5}>
              {activeHolidays.map((w) => {
                const draft = drafts[w.id] ?? { is_holiday: w.is_holiday, holiday_name: w.holiday_name || "" };
                const htype = HOLIDAY_TYPES[detectHolidayType(draft.holiday_name)];
                return (
                  <Chip key={w.id} size="small"
                    label={`${htype.icon} W${w.week_number}${draft.holiday_name ? `: ${draft.holiday_name}` : ""}`}
                    sx={{ bgcolor: htype.bg, color: htype.color, fontWeight: 600, border: `1px solid ${htype.border}`, height: 22 }}
                  />
                );
              })}
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
          </>
        )}

        <Typography variant="caption" fontWeight={700} sx={{ color: "#64748b", display: "block", mb: 0.5 }}>
          Describe your holiday changes
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          size="small"
          placeholder={`e.g. "Add February half-term to weeks 8 and 9"\n"Remove summer holiday from week 12"\n"Mark weeks 3-4 as Easter Holiday"`}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        <Button
          variant="contained"
          size="small"
          startIcon={suggesting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
          onClick={handleGetSuggestions}
          disabled={suggesting || !prompt.trim()}
          sx={{ textTransform: "none", fontWeight: 600, bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK }, mb: 1.5 }}
        >
          {suggesting ? "Getting suggestions…" : "Get Suggestions"}
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: 13 }}>{error}</Alert>
        )}

        {suggestions.length > 0 && (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
              <Typography variant="caption" fontWeight={700} sx={{ color: "#374151" }}>
                {suggestions.length} suggested change{suggestions.length !== 1 ? "s" : ""}
              </Typography>
              <Button size="small" variant="text" onClick={toggleAll}
                sx={{ textTransform: "none", fontWeight: 600, color: PURPLE, fontSize: 12 }}>
                {accepted.size === suggestions.length ? "Deselect All" : "Select All"}
              </Button>
            </Stack>
            <Stack spacing={0.75}>
              {suggestions.map((s) => {
                const currentWeek = weeks.find((w) => w.id === s.week_id);
                const currentDraft = currentWeek
                  ? (drafts[s.week_id] ?? { is_holiday: currentWeek.is_holiday, holiday_name: currentWeek.holiday_name || "" })
                  : null;
                return (
                  <Paper key={s.week_id} elevation={0} sx={{
                    p: 1.25, borderRadius: 2,
                    border: `1px solid ${accepted.has(s.week_id) ? PURPLE : "#e2e8f0"}`,
                    bgcolor: accepted.has(s.week_id) ? PURPLE_SOFT : "#fff",
                  }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <Checkbox
                        size="small"
                        checked={accepted.has(s.week_id)}
                        onChange={() => toggleAccept(s.week_id)}
                        sx={{ p: 0, mt: 0.25, color: "#cbd5e1", "&.Mui-checked": { color: PURPLE } }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5} flexWrap="wrap">
                          <Typography variant="caption" fontWeight={700}>
                            Week {s.week_number}
                          </Typography>
                          {currentWeek && (
                            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                              {currentWeek.start_date} – {currentWeek.end_date}
                            </Typography>
                          )}
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                          {currentDraft && (
                            <HolidayChip isHoliday={currentDraft.is_holiday} holidayName={currentDraft.holiday_name} />
                          )}
                          <ArrowForwardIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
                          <HolidayChip isHoliday={s.is_holiday} holidayName={s.holiday_name} />
                        </Stack>
                        {s.reason && (
                          <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5, fontStyle: "italic" }}>
                            {s.reason}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button variant="outlined" size="small" onClick={handleClose}
          sx={{ textTransform: "none", fontWeight: 600, borderColor: "#e5e7eb", color: "#374151" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleApply}
          disabled={accepted.size === 0}
          sx={{ textTransform: "none", fontWeight: 600, bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK } }}
        >
          Accept {accepted.size > 0 ? `${accepted.size} ` : ""}Selected
        </Button>
      </DialogActions>
    </Dialog>
  );
}
